/**
 * CashFlowChart — Recharts ComposedChart displaying income/expense bars
 * and a balance line over time, with past vs. future visual distinction.
 *
 * Props:
 *   data              : buckets array from /v1/cash-flow response
 *   today             : 'YYYY-MM-DD' string
 *   granularity       : 'day' | 'week' | 'month'
 *   viewMode          : 'all' | 'actual' | 'forecast' | 'budget'
 *   provisionCount    : number of active provisions (for legend note)
 */

import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Info } from 'lucide-react';
import { formatCurrency, formatCurrencyShort } from '../lib/format.js';

// ── Label formatters by granularity ──────────────────────────────────────────

function formatXLabel(periodStart, granularity) {
  if (!periodStart) return '';
  const date = new Date(periodStart + 'T00:00:00');
  if (granularity === 'month') {
    return date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
  }
  if (granularity === 'week') {
    // ISO week number
    const jan4 = new Date(date.getFullYear(), 0, 4);
    const weekNum = Math.ceil(
      ((date - jan4) / 86400000 + jan4.getDay() + 1) / 7
    );
    return `S${String(weekNum).padStart(2, '0')}`;
  }
  // day
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

// ── Custom tooltip ────────────────────────────────────────────────────────────

const SERIES_LABELS = {
  actualIncome: 'Recettes réelles',
  actualExpense: 'Dépenses réelles',
  forecastIncome: 'Recettes prévisionnelles',
  forecastExpense: 'Dépenses prévisionnelles',
  budgetIncome: 'Recettes budget',
  budgetExpense: 'Dépenses budget',
  balancePast: 'Solde (réel)',
  balanceFuture: 'Solde (prévisionnel)',
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const items = payload.filter((p) => p.value !== null && p.value !== undefined);
  return (
    <div className="bg-white rounded-lg shadow-lg border border-slate-200 px-3 py-2 text-xs min-w-[180px]">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {items.map((p) => (
        <p key={p.dataKey} style={{ color: p.color ?? p.stroke }} className="flex justify-between gap-3">
          <span>{SERIES_LABELS[p.dataKey] ?? p.name}</span>
          <span className="tabular-nums font-medium">{formatCurrency(p.value * 100)}</span>
        </p>
      ))}
    </div>
  );
}

// ── Chart data preparation ────────────────────────────────────────────────────

function prepareChartData(buckets, today, granularity) {
  return buckets.map((b) => {
    const isFuture = b.is_future;
    const label = formatXLabel(b.period_start, granularity);
    const isToday = b.period_start <= today && today <= b.period_end;

    return {
      label,
      period_start: b.period_start,
      period_end: b.period_end,
      isFuture,
      isToday,
      // Actual — only for past buckets (values in euros for Recharts)
      actualIncome: !isFuture && b.actual?.income_cents != null
        ? b.actual.income_cents / 100
        : null,
      actualExpense: !isFuture && b.actual?.expense_cents != null
        ? b.actual.expense_cents / 100
        : null,
      // Forecast — for future buckets (or mixed bucket)
      forecastIncome: b.forecast?.income_cents != null
        ? b.forecast.income_cents / 100
        : null,
      forecastExpense: b.forecast?.expense_cents != null
        ? b.forecast.expense_cents / 100
        : null,
      // Budget
      budgetIncome: b.budget?.income_cents != null
        ? b.budget.income_cents / 100
        : null,
      budgetExpense: b.budget?.expense_cents != null
        ? b.budget.expense_cents / 100
        : null,
      // Balance line (split into past and future series)
      balancePast: !isFuture && b.balance_end_cents != null
        ? b.balance_end_cents / 100
        : null,
      balanceFuture: isFuture && b.balance_end_cents != null
        ? b.balance_end_cents / 100
        : null,
      // Keep balance at the boundary point visible on both lines
      balanceBridge: b.balance_end_cents != null
        ? b.balance_end_cents / 100
        : null,
    };
  });
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function CashFlowChart({ data = [], today, granularity = 'month', viewMode = 'all', provisionCount = 0 }) {
  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-[280px] text-sm text-slate-400">
        Pas de données
      </div>
    );
  }

  const chartData = prepareChartData(data, today, granularity);

  // Find the label of the bucket containing today for the ReferenceLine
  const todayBucket = chartData.find((d) => d.isToday);
  const todayLabel = todayBucket?.label ?? null;

  const showActual = viewMode === 'all' || viewMode === 'actual';
  const showForecast = viewMode === 'all' || viewMode === 'forecast';
  const showBudget = viewMode === 'budget' || viewMode === 'all';

  // Build a single bridge dataset for where past meets future
  // (ensure continuity of the balance line at the today boundary)
  const bridgeIdx = chartData.findIndex((d) => d.isFuture);
  const bridgeLabel = bridgeIdx > 0 ? chartData[bridgeIdx - 1].label : null;

  return (
    <div className="w-full" style={{ minHeight: 280 }}>
      {/* Note provisions — s'affiche uniquement quand des provisions sont actives et
          que la série "Prévisionnel" est visible, car les alims provision y sont agrégées */}
      {provisionCount > 0 && (showForecast || viewMode === 'all') && (
        <div className="flex items-start gap-1.5 text-[11px] text-slate-500 mb-2 px-1">
          <Info size={12} className="mt-0.5 shrink-0 text-indigo-400" aria-hidden="true" />
          <span>
            Les alimentations de{' '}
            <span className="font-medium text-indigo-600">
              {provisionCount} provision{provisionCount > 1 ? 's' : ''}
            </span>{' '}
            active{provisionCount > 1 ? 's' : ''} sont incluses dans la série{' '}
            <span className="font-medium">Dépenses prévisionnelles</span>{' '}
            (dédoublonnage automatique avec les transactions réelles).
          </span>
        </div>
      )}
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart
          data={chartData}
          margin={{ top: 8, right: 8, bottom: 4, left: 0 }}
          barCategoryGap="20%"
          barGap={2}
        >
          <defs>
            {/* Striped pattern for forecast bars */}
            <pattern id="stripeIncome" patternUnits="userSpaceOnUse" width={6} height={6} patternTransform="rotate(45)">
              <rect width={6} height={6} fill="#bbf7d0" />
              <line x1={0} y1={0} x2={0} y2={6} stroke="#4ade80" strokeWidth={2} />
            </pattern>
            <pattern id="stripeExpense" patternUnits="userSpaceOnUse" width={6} height={6} patternTransform="rotate(45)">
              <rect width={6} height={6} fill="#fecaca" />
              <line x1={0} y1={0} x2={0} y2={6} stroke="#f87171" strokeWidth={2} />
            </pattern>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />

          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
          />

          <YAxis
            yAxisId="bars"
            tickFormatter={(v) => formatCurrencyShort(v * 100)}
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
            width={60}
          />

          <YAxis
            yAxisId="balance"
            orientation="right"
            tickFormatter={(v) => formatCurrencyShort(v * 100)}
            tick={{ fontSize: 11, fill: '#94a3b8' }}
            tickLine={false}
            axisLine={false}
            width={60}
          />

          <Tooltip content={<CustomTooltip />} />

          <Legend
            wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
            formatter={(v) => SERIES_LABELS[v] ?? v}
          />

          {/* Reference line for today */}
          {todayLabel && (
            <ReferenceLine
              x={todayLabel}
              yAxisId="bars"
              stroke="#6366f1"
              strokeDasharray="4 3"
              strokeWidth={1.5}
              label={{ value: "Auj.", position: 'insideTopRight', fontSize: 10, fill: '#6366f1' }}
            />
          )}

          {/* Actual income (past) */}
          {showActual && (
            <Bar yAxisId="bars" dataKey="actualIncome" name="actualIncome" fill="#166534" radius={[2, 2, 0, 0]} maxBarSize={24} />
          )}

          {/* Actual expense (past, negative values) */}
          {showActual && (
            <Bar yAxisId="bars" dataKey="actualExpense" name="actualExpense" fill="#991b1b" radius={[2, 2, 0, 0]} maxBarSize={24} />
          )}

          {/* Forecast income (future, striped) */}
          {showForecast && (
            <Bar yAxisId="bars" dataKey="forecastIncome" name="forecastIncome" fill="url(#stripeIncome)" radius={[2, 2, 0, 0]} maxBarSize={24} />
          )}

          {/* Forecast expense (future, striped) */}
          {showForecast && (
            <Bar yAxisId="bars" dataKey="forecastExpense" name="forecastExpense" fill="url(#stripeExpense)" radius={[2, 2, 0, 0]} maxBarSize={24} />
          )}

          {/* Budget income (pale green) */}
          {showBudget && (
            <Bar yAxisId="bars" dataKey="budgetIncome" name="budgetIncome" fill="#bbf7d0" radius={[2, 2, 0, 0]} maxBarSize={24} />
          )}

          {/* Budget expense (pale red) */}
          {showBudget && (
            <Bar yAxisId="bars" dataKey="budgetExpense" name="budgetExpense" fill="#fecaca" radius={[2, 2, 0, 0]} maxBarSize={24} />
          )}

          {/* Balance line — past (solid) */}
          <Line
            yAxisId="balance"
            type="monotone"
            dataKey="balancePast"
            name="balancePast"
            stroke="#475569"
            strokeWidth={2}
            dot={false}
            connectNulls
            activeDot={{ r: 4 }}
          />

          {/* Balance line — future (dashed) */}
          <Line
            yAxisId="balance"
            type="monotone"
            dataKey="balanceFuture"
            name="balanceFuture"
            stroke="#475569"
            strokeWidth={2}
            strokeDasharray="5 4"
            dot={false}
            connectNulls
            activeDot={{ r: 4 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
